import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchUsers, fetchCustomer } from './api'; // Assuming this API call exists
import './customers.css';
import CustomerWorkOrderList from './customerworkorderlist';
import NewCustomer from './customers/newcustomer';

const Customers = () => {
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showWorkOrders, setShowWorkOrders] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [mergedNotice, setMergedNotice] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [paginationInfo, setPaginationInfo] = useState({});
  const location = useLocation();
  const navigate = useNavigate();

  // /accounts?customer=<id> opens that customer — works even when already on this page
  // (e.g. from the duplicate report in Admin Settings, which overlays any page).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const customerId = params.get('customer');
    if (!customerId) return;
    // Set after a merge from the duplicate report: ?customer=<kept id>&merged=<count>
    const mergedCount = params.get('merged');
    setMergedNotice(mergedCount ? `Merged ${mergedCount} customers into this one.` : '');
    navigate('/accounts', { replace: true });
    fetchCustomer(customerId)
      .then(fetchedCustomer => {
        setSelectedCustomer(fetchedCustomer);
        setShowWorkOrders(true);
      })
      .catch(error => {
        console.error('Error fetching customer:', error);
      });
  }, [location.search, navigate]);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await fetchUsers(searchQuery, currentPage, 50);
        if (response && response.results) {
          setUsers(response.results);
          setTotalPages(response.total_pages || 1);
          setTotalCount(response.count || 0);
          setPaginationInfo({
            hasNext: response.has_next || false,
            hasPrevious: response.has_previous || false,
            nextPage: response.next_page || null,
            previousPage: response.previous_page || null
          });
          
          // Open the customer a work order linked here from (stored by id — names aren't unique)
          const storedCustomerId = localStorage.getItem('selectedCustomerId');
          if (storedCustomerId) {
            localStorage.removeItem('selectedCustomerId');
            fetchCustomer(storedCustomerId)
              .then(fetchedCustomer => {
                setSelectedCustomer(fetchedCustomer);
                setShowWorkOrders(true);
              })
              .catch(error => {
                console.error('Error fetching customer:', error);
              });
          }
        } else {
          // Handle case where response doesn't have expected structure
          setUsers([]);
          setTotalPages(1);
          setTotalCount(0);
          setPaginationInfo({
            hasNext: false,
            hasPrevious: false,
            nextPage: null,
            previousPage: null
          });
        }
      } catch (err) {
        setError('Failed to load customers. Please try again.');
        console.error('Error loading users:', err);
        setUsers([]);
        setTotalPages(1);
        setTotalCount(0);
        setPaginationInfo({
          hasNext: false,
          hasPrevious: false,
          nextPage: null,
          previousPage: null
        });
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      loadUsers();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, currentPage]);

  // Reset to page 1 when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleCustomerClick = (customer) => {
    setSelectedCustomer(customer);
    console.log(customer)
    setShowWorkOrders(true);
  };

  const handleBack = () => {
    setShowWorkOrders(false);
    setSelectedCustomer(null);
    setMergedNotice('');
  };

  const handleNewCustomerCreated = (newCustomer) => {
    setUsers([...users, newCustomer]);
    setShowNewCustomer(false);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleNextPage = () => {
    if (paginationInfo.hasNext) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (paginationInfo.hasPrevious) {
      setCurrentPage(currentPage - 1);
    }
  };

  const renderCustomerList = () => (
    <div className="customerlist-container">
      
<div className="customerlist-header">
        <h1 className="customerlist-title">Customers</h1>
        <button 
          className="customerlist-create-button"
          onClick={() => setShowNewCustomer(true)}
        >
          <span>+</span> New Customer
        </button>
      </div>
      

      <div className="customerlist-search">
        <input
          className="customerlist-search-input"
          type="text"
          placeholder="⌕ Search Customers"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="customerlist-labels">
        <h5 className="customerlist-labels-header">Name</h5>
        <h5 className="customerlist-labels-header">Phone Number</h5>
        <h5 className="customerlist-labels-header customerlist-email-label">Email</h5>
      </div>

      <div className="customerlist-scroll">
        {isLoading ? (
          <div className="customerlist-loading">Loading customers...</div>
        ) : error ? (
          <div className="customerlist-error">{error}</div>
        ) : users && users.length > 0 ? (
          users.map((customer) => (
            <div
              className="customerlist-row"
              key={customer.id}
              onClick={() => handleCustomerClick(customer)}
            >
              <div className="customerlist-row-data">{customer.name}</div>
              <div className="customerlist-row-data">{customer.phone}</div>
              <div className="customerlist-row-data customerlist-email-data">{customer.email}</div>
            </div>
          ))
        ) : (
          <div className="customerlist-no-results">No customers found</div>
        )}
      </div>

      {/* Mobile floating button */}
      <button 
        className="customerlist-mobile-button"
        onClick={() => setShowNewCustomer(true)}
      >
        +
      </button>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="customerlist-pagination">
          <div className="customerlist-pagination-info">
            Showing {((currentPage - 1) * 50) + 1} to {Math.min(currentPage * 50, totalCount)} of {totalCount} customers
          </div>
          <div className="customerlist-pagination-controls">
            <button 
              className="customerlist-pagination-btn"
              onClick={handlePreviousPage}
              disabled={!paginationInfo.hasPrevious}
            >
              ← Previous
            </button>
            
            <div className="customerlist-pagination-pages">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    className={`customerlist-pagination-page ${
                      pageNum === currentPage ? 'active' : ''
                    }`}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button 
              className="customerlist-pagination-btn"
              onClick={handleNextPage}
              disabled={!paginationInfo.hasNext}
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {showNewCustomer && (
        <NewCustomer
          showModal={showNewCustomer}
          toggleModal={() => setShowNewCustomer(false)}
          onCustomerCreated={handleNewCustomerCreated}
          onExistingCustomerChosen={handleCustomerClick}
        />
      )}
    </div>
  );

  return (
    <div className="customerlist">
      {showWorkOrders && selectedCustomer ? (
        <div className="customer-work-order-container">
          <button className="customerlist-button" onClick={handleBack}>
            <span className="back-arrow">←</span>
            <span className="back-text">Back to Customer List</span>
          </button>
          {mergedNotice && <div className="customers-merged-notice">{mergedNotice}</div>}
          <CustomerWorkOrderList customerId={selectedCustomer.id} />
        </div>
      ) : (
        renderCustomerList()
      )}
    </div>
  );
};

export default Customers;


